<template>
  <div class="mb-8">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-3">
      <button
        v-if="showCollapseToggle"
        type="button"
        class="flex items-center gap-1 text-left"
        @click="collapsedProxy = !collapsedProxy"
      >
        <h3 class="text-lg font-semibold">{{ heading }}</h3>
        <span class="text-gray-500 text-sm">{{ collapsedProxy ? '▸' : '▾' }}</span>
        <span v-if="showInfoIcon" class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.by_ceremony" role="img" aria-label="Info">ⓘ</span>
      </button>
      <h3 v-else class="text-lg font-semibold">{{ heading }} <span v-if="showInfoIcon" class="ml-1 text-gray-400 cursor-help text-sm info-tip" :title="tooltips.by_ceremony" role="img" aria-label="Info">ⓘ</span></h3>
      <div class="flex items-center gap-2">
        <template v-if="!collapsedProxy">
          <template v-if="showDataScopeControl">
            <label class="text-xs text-gray-600">Data</label>
            <select v-model="ceremonyDataScopeProxy" class="border rounded px-2 py-1 text-xs" @change="$emit('ceremony-data-scope-change')">
              <option value="season">Season</option>
              <option value="window">Selected range</option>
            </select>
          </template>
          <label class="text-xs text-gray-600">View</label>
          <select v-model="ceremonyViewModeProxy" class="border rounded px-2 py-1 text-xs">
            <option value="table">Table</option>
            <option value="chart">Chart</option>
          </select>
          <label class="text-xs text-gray-600 ml-2">Sort</label>
          <select v-model="ceremonySortModeProxy" class="border rounded px-2 py-1 text-xs">
            <option value="date_asc">Date (oldest first)</option>
            <option value="date_desc">Date (newest first)</option>
            <option value="views_desc">Performance: Total views</option>
            <option value="shares_desc">Performance: Shares</option>
            <option value="downloads_desc">Performance: Downloads</option>
          </select>
          <template v-if="ceremonyViewMode === 'chart'">
            <label class="text-xs text-gray-600 ml-2">Metric</label>
            <select v-model="ceremonyChartMetricProxy" class="border rounded px-2 py-1 text-xs">
              <option value="total_views">Total views</option>
              <option value="unique_views">Unique views</option>
              <option value="total_shares">Shares</option>
              <option value="total_downloads">Downloads</option>
            </select>
          </template>
        </template>
      </div>
    </div>

    <template v-if="!collapsedProxy">
    <div v-if="showScopeHint" class="text-xs text-gray-500 mb-3">
      <template v-if="showDataScopeControl">
        <template v-if="ceremonyDataScope === 'season'">{{ seasonScopeHint }}</template>
        <template v-else>{{ windowScopeHint }}</template>
      </template>
      <template v-else>
        {{ fixedScopeHint }}
      </template>
    </div>

    <div v-if="isCeremonyBreakdownLoading" class="text-sm text-gray-500">Loading ceremony breakdown...</div>
    <div v-else-if="ceremonyBreakdownError" class="text-sm text-red-600">{{ ceremonyBreakdownError }}</div>
    <div v-else-if="!ceremonyRowsSorted.length" class="text-sm text-gray-500">No ceremony-level data in this scope.</div>

    <div v-else-if="ceremonyViewMode === 'table'" class="overflow-x-auto border rounded-lg bg-white">
      <table class="min-w-full text-sm">
        <thead class="bg-gray-50 text-gray-600">
          <tr>
            <th class="text-left px-3 py-2">Ceremony</th>
            <th class="text-right px-3 py-2">Unique views</th>
            <th class="text-right px-3 py-2">Total views</th>
            <th class="text-right px-3 py-2">Shares</th>
            <th class="text-right px-3 py-2">Downloads</th>
            <th class="text-left px-3 py-2">Most popular clip</th>
            <th class="text-right px-3 py-2">Top clips</th>
          </tr>
        </thead>
        <tbody v-for="row in ceremonyRowsSorted" :key="row.ceremony_id">
          <tr class="border-t align-top">
            <td class="px-3 py-2">
              <div class="font-medium">{{ ceremonyTitle(row) }}</div>
              <div class="text-xs text-gray-500">{{ row.ceremony_id }}</div>
            </td>
            <td class="px-3 py-2 text-right">{{ row.unique_views }}</td>
            <td class="px-3 py-2 text-right font-medium">{{ row.total_views }}</td>
            <td class="px-3 py-2 text-right">{{ row.total_shares }}</td>
            <td class="px-3 py-2 text-right">{{ row.total_downloads }}</td>
            <td class="px-3 py-2">
              <template v-if="isCeremonyTopClipsLoading(row.ceremony_id)"><span class="text-gray-500">Loading...</span></template>
              <template v-else-if="row.top_clips && row.top_clips.length">
                <a v-if="row.top_clips[0].clip_path" :href="row.top_clips[0].clip_path" target="_blank" rel="noopener noreferrer" class="text-blue-700 hover:underline">{{ clipName(row.top_clips[0]) }}</a>
                <span v-else>{{ clipName(row.top_clips[0]) }}</span>
                <span class="text-xs text-gray-500 ml-1">({{ row.top_clips[0].total_views }} views)</span>
              </template>
              <span v-else class="text-gray-500">Expand to load</span>
            </td>
            <td class="px-3 py-2 text-right">
              <button class="px-2 py-1 border rounded text-xs hover:bg-gray-50" :disabled="isCeremonyTopClipsLoading(row.ceremony_id)" @click="$emit('toggle-ceremony-expand', row)">
                {{ isCeremonyExpanded(row.ceremony_id) ? 'Hide' : 'Show top 3' }}
              </button>
            </td>
          </tr>
          <tr v-if="isCeremonyExpanded(row.ceremony_id)" class="border-t bg-gray-50">
            <td colspan="7" class="px-3 py-3">
              <div class="text-xs text-gray-600 mb-2">Top 3 clips by total views</div>
              <div v-if="isCeremonyTopClipsLoading(row.ceremony_id)" class="text-sm text-gray-500">Loading top clips...</div>
              <div v-else-if="!(row.top_clips || []).length" class="text-sm text-gray-500">No clip view data for this ceremony in this scope.</div>
              <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div v-for="clip in row.top_clips" :key="clip.submission_id || clip.rank" class="border rounded bg-white px-3 py-2">
                  <div class="text-xs text-gray-500 mb-1">#{{ clip.rank }}</div>
                  <a v-if="clip.clip_path" :href="clip.clip_path" target="_blank" rel="noopener noreferrer" class="text-sm text-blue-700 hover:underline block">{{ clipName(clip) }}</a>
                  <div v-else class="text-sm">{{ clipName(clip) }}</div>
                  <div class="text-xs text-gray-500 mt-1">{{ clip.total_views }} views</div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-else class="border rounded-lg bg-white p-4">
      <div class="text-xs text-gray-600 mb-3">Visual comparison by ceremony ({{ chartMetricLabel }})</div>
      <div class="space-y-2 max-h-[420px] overflow-auto pr-1">
        <div v-for="row in ceremonyRowsSorted" :key="`chart-${row.ceremony_id}`">
          <div class="flex justify-between text-xs mb-1">
            <span class="truncate max-w-[70%]" :title="ceremonyTitle(row)">{{ ceremonyTitle(row) }}</span>
            <span>{{ metricValue(row, ceremonyChartMetric) }}</span>
          </div>
          <div class="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div class="h-2 rounded-full bg-gradient-to-r from-black to-gray-600" :style="{ width: ceremonyMetricWidth(row, ceremonyChartMetric) }"></div>
          </div>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'CeremonyBreakdownSection',
  props: {
    heading: { type: String, default: 'By ceremony' },
    showInfoIcon: { type: Boolean, default: true },
    showDataScopeControl: { type: Boolean, default: true },
    showScopeHint: { type: Boolean, default: true },
    showCollapseToggle: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    seasonScopeHint: { type: String, default: 'Ceremony totals scoped to selected season ceremonies.' },
    windowScopeHint: { type: String, default: 'Ceremony totals restricted to the selected historical time range.' },
    fixedScopeHint: { type: String, default: 'Ceremony totals scoped to selected season ceremonies.' },
    ceremonyDataScope: { type: String, default: 'season' },
    ceremonyViewMode: { type: String, default: 'table' },
    ceremonySortMode: { type: String, default: 'date_asc' },
    ceremonyChartMetric: { type: String, default: 'total_views' },
    isCeremonyBreakdownLoading: { type: Boolean, default: false },
    ceremonyBreakdownError: { type: String, default: '' },
    ceremonyRowsSorted: { type: Array, default: () => [] },
    chartMetricLabel: { type: String, default: '' },
    ceremonyTitleFn: { type: Function, required: true },
    isCeremonyTopClipsLoadingFn: { type: Function, required: true },
    clipNameFn: { type: Function, required: true },
    isCeremonyExpandedFn: { type: Function, required: true },
    metricValueFn: { type: Function, required: true },
    ceremonyMetricWidthFn: { type: Function, required: true },
    tooltips: { type: Object, default: () => ({}) },
  },
  computed: {
    ceremonyDataScopeProxy: {
      get() { return this.ceremonyDataScope },
      set(value) { this.$emit('update:ceremonyDataScope', String(value || 'season')) },
    },
    collapsedProxy: {
      get() { return this.collapsed },
      set(value) { this.$emit('update:collapsed', Boolean(value)) },
    },
    ceremonyViewModeProxy: {
      get() { return this.ceremonyViewMode },
      set(value) { this.$emit('update:ceremonyViewMode', String(value || 'table')) },
    },
    ceremonySortModeProxy: {
      get() { return this.ceremonySortMode },
      set(value) { this.$emit('update:ceremonySortMode', String(value || 'date_asc')) },
    },
    ceremonyChartMetricProxy: {
      get() { return this.ceremonyChartMetric },
      set(value) { this.$emit('update:ceremonyChartMetric', String(value || 'total_views')) },
    },
  },
  methods: {
    ceremonyTitle(row) { return this.ceremonyTitleFn(row) },
    isCeremonyTopClipsLoading(ceremonyId) { return this.isCeremonyTopClipsLoadingFn(ceremonyId) },
    clipName(clip) { return this.clipNameFn(clip) },
    isCeremonyExpanded(ceremonyId) { return this.isCeremonyExpandedFn(ceremonyId) },
    metricValue(row, metric) { return this.metricValueFn(row, metric) },
    ceremonyMetricWidth(row, metric) { return this.ceremonyMetricWidthFn(row, metric) },
  },
}
</script>
